import { useRef, useState, type ChangeEvent } from 'react';
import { useAppConfig, useChangePassword, useRemovePhoto, useUpdateProfile, useUploadPhoto } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { PHOTO_ACCEPT_ATTRIBUTE, PHOTO_TYPES } from '@/lib/files';
import { ROLE_LABELS } from '@/lib/format';
import { preparePhoto } from '@/lib/images';
import { Avatar, Field, Modal } from './ui';
import { IconCamera, IconCheck, IconKey, IconTrash, IconUpload } from './Icons';

/**
 * "Minha conta": qualquer pessoa troca a própria foto, o próprio nome e a
 * própria senha sem depender de um administrador. Login e papel ficam só
 * para leitura.
 */
export function AccountModal({ onClose }: { onClose: () => void }) {
  const { user, logout, refreshUser, applyUser } = useAuth();
  const toast = useToast();
  const { data: config } = useAppConfig();
  const updateProfile = useUpdateProfile();
  const changePassword = useChangePassword();
  const uploadPhoto = useUploadPhoto();
  const removePhoto = useRemovePhoto();
  const photoInput = useRef<HTMLInputElement>(null);
  const [preparingPhoto, setPreparingPhoto] = useState(false);

  const [name, setName] = useState(user?.name ?? '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  if (!user) return null;

  const nameChanged = name.trim() !== user.name;
  const photoBusy = preparingPhoto || uploadPhoto.isPending || removePhoto.isPending;
  // Sem o R2 configurado no servidor, a seção avisa em vez de oferecer o envio.
  const photosEnabled = config?.photosEnabled !== false;

  async function choosePhoto(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = ''; // escolher o mesmo arquivo de novo também dispara
    if (!file) return;
    if (!PHOTO_TYPES.includes(file.type)) {
      toast.error('A foto precisa ser uma imagem JPEG, PNG ou WEBP.');
      return;
    }

    setPreparingPhoto(true);
    try {
      const photo = await preparePhoto(file);
      setPreparingPhoto(false);
      const { user: updated } = await uploadPhoto.mutateAsync(photo);
      applyUser(updated);
      toast.success('Foto atualizada.');
    } catch (caught) {
      toast.fromError(caught);
    } finally {
      setPreparingPhoto(false);
    }
  }

  async function deletePhoto() {
    try {
      const { user: updated } = await removePhoto.mutateAsync();
      applyUser(updated);
      toast.success('Foto removida.');
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  async function saveName() {
    if (name.trim().length < 2) {
      toast.error('O nome precisa ter ao menos 2 caracteres.');
      return;
    }
    try {
      await updateProfile.mutateAsync({ name: name.trim() });
      await refreshUser();
      toast.success('Nome atualizado.');
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  async function savePassword() {
    if (!currentPassword) return setPasswordError('Informe a senha atual.');
    if (newPassword.length < 8) return setPasswordError('A nova senha precisa ter ao menos 8 caracteres.');
    if (!/[a-zA-Z]/.test(newPassword) || !/\d/.test(newPassword)) {
      return setPasswordError('A nova senha precisa ter letras e números.');
    }
    if (newPassword !== confirmation) return setPasswordError('A confirmação não bate com a nova senha.');
    setPasswordError(null);

    try {
      await changePassword.mutateAsync({ currentPassword, newPassword });
      // O servidor encerrou todas as sessões, inclusive esta: o próximo passo
      // é entrar de novo, já com a senha nova.
      toast.success('Senha alterada. Entre de novo com a senha nova.');
      onClose();
      await logout();
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  return (
    <Modal title="Minha conta" width={500} onClose={onClose}>
      <section className="photo-row">
        <button
          type="button"
          className="photo-picker"
          onClick={() => photoInput.current?.click()}
          disabled={photoBusy || !photosEnabled}
          aria-label={user.photoUrl ? 'Trocar a foto de perfil' : 'Escolher uma foto de perfil'}
          title={user.photoUrl ? 'Trocar foto' : 'Escolher foto'}
        >
          <Avatar name={user.name} color={user.avatarColor} photo={user.photoUrl} size={72} />
          {photosEnabled ? (
            <span className="photo-picker-badge" aria-hidden>
              {photoBusy ? <span className="spinner" /> : <IconCamera size={14} />}
            </span>
          ) : null}
        </button>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Foto de perfil</div>
          <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            {photosEnabled
              ? 'Aparece no menu, no ranking e nos comprovantes. O centro da imagem vira um quadrado.'
              : 'O envio de fotos ainda não está ligado neste servidor.'}
          </div>
          {photosEnabled ? (
            <div className="photo-actions">
              <button type="button" className="btn btn-sm" onClick={() => photoInput.current?.click()} disabled={photoBusy}>
                <IconUpload /> {user.photoUrl ? 'Trocar foto' : 'Escolher foto'}
              </button>
              {user.photoUrl ? (
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => void deletePhoto()} disabled={photoBusy}>
                  <IconTrash /> Remover
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
        <input
          ref={photoInput}
          type="file"
          accept={PHOTO_ACCEPT_ATTRIBUTE}
          hidden
          onChange={(event) => void choosePhoto(event)}
        />
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 14, borderTop: '1px solid var(--border)', paddingTop: 18 }}>
        <div className="grid grid2">
          <Field label="Usuário" hint="Quem muda o usuário é um administrador.">
            <input className="inp num" value={user.login} disabled readOnly />
          </Field>
          <Field label="Papel">
            <input className="inp" value={ROLE_LABELS[user.role]} disabled readOnly />
          </Field>
        </div>

        <Field label="Nome">
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="inp" value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" />
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void saveName()}
              disabled={!nameChanged || updateProfile.isPending}
            >
              {updateProfile.isPending ? <span className="spinner" /> : <IconCheck />} Salvar
            </button>
          </div>
        </Field>
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 14, borderTop: '1px solid var(--border)', paddingTop: 18 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Trocar senha</div>
          <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            Ao trocar, todas as sessões abertas são encerradas — inclusive esta.
          </div>
        </div>

        <Field label="Senha atual">
          <input
            className="inp"
            type="password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            autoComplete="current-password"
          />
        </Field>
        <div className="grid grid2">
          <Field label="Nova senha" hint="Mínimo 8 caracteres, com letras e números.">
            <input
              className="inp"
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              autoComplete="new-password"
            />
          </Field>
          <Field label="Confirmar nova senha">
            <input
              className="inp"
              type="password"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              autoComplete="new-password"
            />
          </Field>
        </div>

        {passwordError ? <div className="alert alert-error" role="alert">{passwordError}</div> : null}

        <button
          type="button"
          className="btn"
          style={{ alignSelf: 'flex-start' }}
          onClick={() => void savePassword()}
          disabled={changePassword.isPending}
        >
          {changePassword.isPending ? <span className="spinner" /> : <IconKey />} Trocar senha
        </button>
      </section>
    </Modal>
  );
}
